import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Badge } from "primereact/badge";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";

const PaymentsListModal = ({
  visible,
  onHide,
  invoice,
  facilityId,
  onPaymentCancelled,
}) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const statusOptions = [
    { label: "All Statuses", value: null },
    { label: "Pending", value: "pending" },
    { label: "Paid", value: "paid" },
    { label: "Overpaid", value: "overpaid" },
    { label: "Rejected", value: "rejected" },
  ];

  useEffect(() => {
    if (visible && invoice) {
      fetchPayments();
    }
  }, [visible, invoice, filterStatus]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const endpoint = `/api/core/billing/facility/${facilityId}/invoices/${invoice._id}/payments${filterStatus ? `?status=${filterStatus}` : ""}`;
      console.log("[Payments] Fetching from:", endpoint);

      let response;
      try {
        response = await makeRequest2(endpoint, "GET");
      } catch (apiError) {
        console.error("[Payments] API Error:", apiError);

        // Check if it's a 404 (endpoint not found)
        if (apiError.message?.includes("404")) {
          toastify(
            "Payment endpoint not configured yet. Please contact support.",
            "error",
          );
          setLoading(false);
          return;
        }
        throw apiError;
      }

      console.log("[Payments] Raw response:", response);
      console.log("[Payments] Response type:", typeof response);
      console.log(
        "[Payments] Response keys:",
        response ? Object.keys(response) : "null",
      );

      // Handle different response structures
      let paymentsData = [];
      let summaryData = null;

      if (Array.isArray(response)) {
        // Response is directly an array of payments
        paymentsData = response;
        console.log(
          "[Payments] Response is array, length:",
          paymentsData.length,
        );
      } else if (response && response.payments) {
        // Response has payments property
        paymentsData = response.payments;
        summaryData = response.paymentSummary;
        console.log(
          "[Payments] Response has payments property, length:",
          paymentsData.length,
        );
      } else if (response && response.success !== false) {
        // Response might be a success object without explicit payments array
        paymentsData = response.data?.payments || response.data || [];
        summaryData = response.data?.paymentSummary || response.paymentSummary;
        console.log(
          "[Payments] Response success format, length:",
          paymentsData.length,
        );
      }

      console.log("[Payments] Setting payments:", paymentsData);
      console.log("[Payments] Setting summary:", summaryData);

      setPayments(paymentsData);
      setPaymentSummary(summaryData);

      if (paymentsData.length === 0) {
        console.warn("[Payments] No payments found in response");
        toastify("No payments found for this invoice", "info");
      } else {
        console.log(
          `[Payments] Successfully loaded ${paymentsData.length} payment(s)`,
        );
      }
    } catch (error) {
      console.error("[Payments] Error fetching payments:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      toastify(`Error loading payments: ${errorMessage}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const getStatusSeverity = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "overpaid":
        return "info";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const handleCancelPayment = (payment) => {
    setSelectedPayment(payment);
    setRejectionReason("");
    setShowCancelDialog(true);
  };

  const confirmCancelPayment = async () => {
    if (!rejectionReason || rejectionReason.trim() === "") {
      toastify("Please provide a reason for cancellation", "error");
      return;
    }

    try {
      setCancelling(true);
      const endpoint = `/api/core/billing/facility/${facilityId}/invoices/${invoice._id}/payments/${selectedPayment._id}`;
      const response = await makeRequest2(endpoint, "DELETE", {
        rejectedReason: rejectionReason,
      });

      if (response && (response.success !== false || response.payment)) {
        const message = response.message || "Payment cancelled successfully";
        toastify(message, "success");
        setShowCancelDialog(false);
        setSelectedPayment(null);
        setRejectionReason("");
        fetchPayments();
        if (onPaymentCancelled) {
          onPaymentCancelled(response);
        }
      } else {
        const errorMsg =
          response?.error || response?.details || "Failed to cancel payment";
        toastify(errorMsg, "error");
      }
    } catch (error) {
      console.error("Error cancelling payment:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      toastify(`Failed to cancel payment: ${errorMessage}`, "error");
    } finally {
      setCancelling(false);
    }
  };

  const handleViewProof = (payment) => {
    if (payment.proofOfPaymentUrl) {
      window.open(payment.proofOfPaymentUrl, "_blank");
    } else {
      toastify("No proof of payment available", "info");
    }
  };

  const formatCurrency = (value) => {
    return `KES ${value?.toLocaleString() || 0}`;
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Badge
        value={rowData.status}
        severity={getStatusSeverity(rowData.status)}
        className="text-capitalize"
      />
    );
  };

  const amountBodyTemplate = (rowData) => {
    return (
      <span className="fw-bold">{formatCurrency(rowData.amountPaid)}</span>
    );
  };

  const dateBodyTemplate = (rowData) => {
    return new Date(rowData.paymentDate).toLocaleDateString();
  };

  const methodBodyTemplate = (rowData) => {
    return <span className="text-capitalize">{rowData.paymentMethod}</span>;
  };

  const proofBodyTemplate = (rowData) => {
    if (rowData.proofOfPaymentUrl) {
      return (
        <Button
          icon="ti ti-file-download"
          className="p-button-sm p-button-text p-button-info"
          onClick={() => handleViewProof(rowData)}
          tooltip="View Proof"
        />
      );
    }
    return <span className="text-muted">No proof</span>;
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        {rowData.proofOfPaymentUrl && (
          <Button
            icon="ti ti-eye"
            className="p-button-sm p-button-text p-button-info"
            onClick={() => handleViewProof(rowData)}
            tooltip="View Proof"
          />
        )}
        {rowData.status !== "rejected" && (
          <Button
            icon="ti ti-x"
            className="p-button-sm p-button-text p-button-danger"
            onClick={() => handleCancelPayment(rowData)}
            tooltip="Cancel Payment"
          />
        )}
      </div>
    );
  };

  const commentsBodyTemplate = (rowData) => {
    if (rowData.status === "rejected" && rowData.rejectedReason) {
      return (
        <div className="text-danger">
          <small>
            <i className="ti ti-alert-circle me-1"></i>
            {rowData.rejectedReason}
          </small>
        </div>
      );
    }
    return rowData.comments ? (
      <small className="text-muted">{rowData.comments}</small>
    ) : (
      <span className="text-muted">-</span>
    );
  };

  return (
    <>
      <Dialog
        visible={visible}
        onHide={onHide}
        header={`Payments for Invoice ${invoice?.invoiceNumber}`}
        modal
        style={{ width: "90vw", maxWidth: "1200px" }}
        maximizable
      >
        <div className="p-fluid">
          {/* Payment Summary Section */}
          {paymentSummary && (
            <div className="mb-4 p-3 bg-light rounded">
              <h6 className="mb-3">
                <i className="ti ti-report-money me-2"></i>Payment Summary
              </h6>
              <div className="grid">
                <div className="col-12 md:col-3">
                  <div className="text-center p-3 border-round bg-white">
                    <div className="text-muted mb-1">Invoice Amount</div>
                    <div className="fs-5 fw-bold text-primary">
                      {formatCurrency(paymentSummary.invoiceAmount)}
                    </div>
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="text-center p-3 border-round bg-white">
                    <div className="text-muted mb-1">Total Paid</div>
                    <div className="fs-5 fw-bold text-success">
                      {formatCurrency(paymentSummary.totalPaid)}
                    </div>
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="text-center p-3 border-round bg-white">
                    <div className="text-muted mb-1">Remaining Balance</div>
                    <div className="fs-5 fw-bold text-warning">
                      {formatCurrency(paymentSummary.remainingBalance)}
                    </div>
                  </div>
                </div>
                <div className="col-12 md:col-3">
                  <div className="text-center p-3 border-round bg-white">
                    <div className="text-muted mb-1">Payments Count</div>
                    <div className="fs-5 fw-bold text-info">
                      {paymentSummary.validPaymentsCount || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filter Section */}
          <div className="flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Payment History</h6>
            <Dropdown
              value={filterStatus}
              options={statusOptions}
              onChange={(e) => setFilterStatus(e.value)}
              placeholder="Filter by Status"
              style={{ width: "200px" }}
            />
          </div>

          {/* Payments Table */}
          {loading ? (
            <div className="text-center py-5">
              <ProgressSpinner />
              <p className="text-muted mt-2">Loading payments...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-5">
              <i className="ti ti-inbox fs-1 text-muted mb-3"></i>
              <h6 className="text-muted">No payments found</h6>
              <p className="text-muted">
                {filterStatus
                  ? `No payments with status: ${filterStatus}`
                  : "This invoice has no payments yet"}
              </p>
              <p className="text-muted">
                <small>
                  Invoice Amount: {formatCurrency(invoice?.amount || 0)}
                </small>
              </p>
            </div>
          ) : (
            <DataTable
              value={payments}
              paginator
              rows={10}
              emptyMessage="No payments found"
              stripedRows
              showGridlines
              responsiveLayout="scroll"
            >
              <Column
                field="paymentDate"
                header="Date"
                body={dateBodyTemplate}
                sortable
                style={{ width: "120px" }}
              />
              <Column
                field="amountPaid"
                header="Amount"
                body={amountBodyTemplate}
                sortable
                style={{ width: "150px" }}
              />
              <Column
                field="paymentMethod"
                header="Method"
                body={methodBodyTemplate}
                style={{ width: "120px" }}
              />
              <Column
                field="status"
                header="Status"
                body={statusBodyTemplate}
                sortable
                style={{ width: "120px" }}
              />
              <Column
                header="Proof"
                body={proofBodyTemplate}
                style={{ width: "100px", textAlign: "center" }}
              />
              <Column
                header="Comments"
                body={commentsBodyTemplate}
                style={{ minWidth: "200px" }}
              />
              <Column
                header="Actions"
                body={actionsBodyTemplate}
                style={{ width: "120px", textAlign: "center" }}
              />
            </DataTable>
          )}
        </div>
      </Dialog>

      {/* Cancel Payment Dialog */}
      <Dialog
        visible={showCancelDialog}
        onHide={() => {
          setShowCancelDialog(false);
          setSelectedPayment(null);
          setRejectionReason("");
        }}
        header="Cancel Payment"
        modal
        style={{ width: "500px" }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Back"
              icon="ti ti-arrow-left"
              className="p-button-text"
              onClick={() => {
                setShowCancelDialog(false);
                setSelectedPayment(null);
                setRejectionReason("");
              }}
              disabled={cancelling}
            />
            <Button
              label="Confirm Cancellation"
              icon="ti ti-check"
              className="p-button-danger"
              onClick={confirmCancelPayment}
              disabled={cancelling || !rejectionReason}
              loading={cancelling}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <div className="mb-3 p-3 bg-light rounded">
            <div className="mb-2">
              <strong>Payment Amount:</strong>{" "}
              {formatCurrency(selectedPayment?.amountPaid)}
            </div>
            <div className="mb-2">
              <strong>Payment Date:</strong>{" "}
              {selectedPayment?.paymentDate &&
                new Date(selectedPayment.paymentDate).toLocaleDateString()}
            </div>
            <div>
              <strong>Payment Method:</strong>{" "}
              <span className="text-capitalize">
                {selectedPayment?.paymentMethod}
              </span>
            </div>
          </div>

          <div className="field">
            <label htmlFor="rejectionReason" className="fw-bold">
              Reason for Cancellation <span className="text-danger">*</span>
            </label>
            <InputTextarea
              id="rejectionReason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              placeholder="Please provide a detailed reason for cancelling this payment..."
              className="w-100"
              disabled={cancelling}
            />
            <small className="text-muted">
              This action cannot be undone. The invoice status will be
              recalculated.
            </small>
          </div>
        </div>
      </Dialog>

      <ConfirmDialog />
    </>
  );
};

export default PaymentsListModal;
